'use client';

import NextLink from 'next/link';
import { useTranslations, useLocale } from 'next-intl';
import {
  Box,
  Container,
  Typography,
  Paper,
  Button,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { LandingHeader, LandingFooter } from '../../../features/landing';

/**
 * PrivacyPageContent - Client component for the Privacy Policy page
 *
 * Covers:
 * - Data collection
 * - Third-party sharing (OpenAI, operational services only)
 * - No data selling
 * - User rights
 *
 * Story 6.7: Landing Page Header And Footer For Credibility
 */
export function PrivacyPageContent() {
  const t = useTranslations('creator.privacy');
  const locale = useLocale();

  // Format current date for "last updated"
  const lastUpdatedDate = new Date().toLocaleDateString(locale === 'he' ? 'he-IL' : locale === 'es' ? 'es-ES' : 'en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      <LandingHeader />

      <Container maxWidth="md" sx={{ py: { xs: 4, md: 6 }, flex: 1 }}>
        {/* Page Title */}
        <Typography
          variant="h3"
          component="h1"
          sx={{
            fontWeight: 700,
            mb: 2,
            textAlign: 'center',
          }}
        >
          {t('title')}
        </Typography>

        {/* Last Updated */}
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ mb: 4, textAlign: 'center' }}
        >
          {t('lastUpdated', { date: lastUpdatedDate })}
        </Typography>

        {/* Data Collection Section */}
        <Paper
          elevation={0}
          sx={{
            p: { xs: 3, md: 4 },
            mb: 3,
            bgcolor: 'grey.50',
            borderRadius: 2,
          }}
        >
          <Typography variant="h6" component="h2" sx={{ fontWeight: 600, mb: 2 }}>
            {t('dataCollection')}
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.7 }}>
            {t('dataCollectionText')}
          </Typography>
        </Paper>

        {/* Data Sharing Section */}
        <Paper
          elevation={0}
          sx={{
            p: { xs: 3, md: 4 },
            mb: 3,
            bgcolor: 'grey.50',
            borderRadius: 2,
          }}
        >
          <Typography variant="h6" component="h2" sx={{ fontWeight: 600, mb: 2 }}>
            {t('dataSharing')}
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.7, mb: 2 }}>
            {t('dataSharingText')}
          </Typography>
          <List dense>
            <ListItem sx={{ py: 0.5 }}>
              <ListItemIcon sx={{ minWidth: 36 }}>
                <CheckCircleOutlineIcon color="primary" fontSize="small" />
              </ListItemIcon>
              <ListItemText
                primary={t('dataSharingOpenAI')}
                primaryTypographyProps={{ color: 'text.secondary' }}
              />
            </ListItem>
            <ListItem sx={{ py: 0.5 }}>
              <ListItemIcon sx={{ minWidth: 36 }}>
                <CheckCircleOutlineIcon color="primary" fontSize="small" />
              </ListItemIcon>
              <ListItemText
                primary={t('dataSharingOperational')}
                primaryTypographyProps={{ color: 'text.secondary' }}
              />
            </ListItem>
          </List>
        </Paper>

        {/* No Data Selling Section */}
        <Paper
          elevation={0}
          sx={{
            p: { xs: 3, md: 4 },
            mb: 3,
            bgcolor: 'success.50',
            borderRadius: 2,
            border: 1,
            borderColor: 'success.200',
          }}
        >
          <Typography variant="h6" component="h2" sx={{ fontWeight: 600, mb: 2 }}>
            {t('noSelling')}
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.7 }}>
            {t('noSellingText')}
          </Typography>
        </Paper>

        {/* Data Security Section */}
        <Paper
          elevation={0}
          sx={{
            p: { xs: 3, md: 4 },
            mb: 3,
            bgcolor: 'grey.50',
            borderRadius: 2,
          }}
        >
          <Typography variant="h6" component="h2" sx={{ fontWeight: 600, mb: 2 }}>
            {t('dataSecurity')}
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.7 }}>
            {t('dataSecurityText')}
          </Typography>
        </Paper>

        {/* Your Rights Section */}
        <Paper
          elevation={0}
          sx={{
            p: { xs: 3, md: 4 },
            mb: 4,
            bgcolor: 'grey.50',
            borderRadius: 2,
          }}
        >
          <Typography variant="h6" component="h2" sx={{ fontWeight: 600, mb: 2 }}>
            {t('yourRights')}
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.7 }}>
            {t('yourRightsText')}
          </Typography>
        </Paper>

        <Divider sx={{ my: 4 }} />

        {/* Back to Home Button */}
        <Box sx={{ textAlign: 'center' }}>
          <Button
            component={NextLink}
            href={`/${locale}`}
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            size="large"
            sx={{ textTransform: 'none' }}
          >
            {t('backToHome')}
          </Button>
        </Box>
      </Container>

      <LandingFooter />
    </Box>
  );
}

export default PrivacyPageContent;
